import {Context} from '@datadog/browser-core';
import {findKey, isObject, mapValues} from 'lodash';
import memoize from 'memoize-one';

import {tryParseUrl} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {hasProperty} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {apiRoutesRegexesMap} from '../bugsnag/routeRegexesMap';
import {HttpResponse, RequestMethodsEnum, ResponseHeaders} from './httpBase';

/*
 * Constants.
 */

const messagePrefix = '[http] ';
const routesRegExpMap = mapValues(apiRoutesRegexesMap, (regexString) => new RegExp(regexString));

/*
 * Types.
 */

export type HttpErrorType =
  | HttpError
  | HttpTimeoutError
  | HttpNetworkError
  | HttpResponseError
  | HttpEmptyResponseError;

/*
 * Classes.
 */

/** Base class for all HTTP errors. */
export abstract class HttpError extends CustomError {
  constructor(method: RequestMethodsEnum, path: string, message: string) {
    super(`${messagePrefix}${message}`);

    this.method = method;
    this.path = path;

    this.findApiRouteMemoized = memoize(() => findApiRoute(path));
  }

  readonly method: RequestMethodsEnum;
  readonly path: string;

  private readonly findApiRouteMemoized: () => string | undefined;

  get apiRoute(): string | undefined {
    return this.findApiRouteMemoized();
  }

  isLogged: boolean = false;
  abstract readonly logMessage: string;

  get logContext(): Context {
    return {
      method: this.method,
      path: this.path,
      api_route: this.apiRoute,
    };
  }
}

export class HttpTimeoutError extends HttpError {
  constructor(method: RequestMethodsEnum, path: string, durationMs: number) {
    super(method, path, 'The request timed-out.');

    this.durationMs = durationMs;
  }

  readonly durationMs: number;

  readonly logMessage: string = 'HTTP timeout error';

  get logContext(): Context {
    return {
      ...super.logContext,
      duration: this.durationMs,
    };
  }
}

export class HttpNetworkError extends HttpError {
  constructor(method: RequestMethodsEnum, path: string) {
    super(method, path, 'The request failed due to a network error.');
  }

  readonly logMessage: string = 'HTTP network error';
}

export class HttpResponseError extends HttpError {
  constructor(method: RequestMethodsEnum, path: string, response: HttpResponse) {
    super(method, path, `The request failed — ${response.status} — ${path}`);

    const {data} = response;
    this.status = response.status;
    this.code = hasProperty(data, 'code') ? String(data.code) : '';
    this.reason = hasProperty(data, 'reason') ? String(data.reason) : '';
    this.body = isObject(data) ? data : {};
    this.file = computeFileFromResponse(response);
    this.headers = response.headers;
  }

  get groupingHash(): string | undefined {
    const {code, apiRoute} = this;

    if (apiRoute) {
      return `http-${code}-${apiRoute}`;
    }

    return undefined;
  }

  readonly status: number;
  readonly code: string;
  readonly reason: string;
  readonly body: unknown;
  readonly file?: File;
  readonly headers: ResponseHeaders;

  readonly logMessage: string = 'HTTP response error';

  get logContext(): Context {
    return {
      ...super.logContext,
      status: this.status,
      code: this.code,
      reason: this.reason,
    };
  }
}

export class HttpEmptyResponseError extends HttpResponseError {
  readonly logMessage: string = 'HTTP empty response error';
}

/*
 * Helpers.
 */

/**
 * A transient error is a temporary error that is likely to disappear soon. Network errors and timeouts are errors
 * that do not need to be reported to bugsnag but should be reported to datadog.
 */
export function isTransientError(error: any) {
  return error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
}

export function isHttpTooManyRequestsError(error: any) {
  return error instanceof HttpResponseError && error.status === 429;
}

function findApiRoute(path: string) {
  const pathUrl = tryParseUrl(path, 'http://localhost');

  if (!pathUrl) {
    return undefined;
  }

  const {pathname} = pathUrl;

  const meaningfulPath = findKey(routesRegExpMap, (r) => r.test(pathname));

  return meaningfulPath;
}

function computeFileFromResponse(response: HttpResponse) {
  const contentType = response.headers['content-type'];

  if (contentType && contentType.includes('text/csv')) {
    // The content-disposition should contain the new file name
    const fileName = response.headers['content-disposition']?.split('filename="')[1].slice(0, -1);
    // Construct the new file object with the response
    return new File([new Blob([response.data as string], {type: 'text/csv'})], fileName ?? 'erred-file.csv', {
      type: 'text/csv',
    });
  }
  return undefined;
}
